/**
 * transforms/connectorFlush.ts — G5/C1 FLUSH: pure positioning functions
 * vertical of the locks with respect to the connector plane (PURE math, zero three.js).
 *
 * Boundary rule (SPEC §6, PLAN §1.2): `transforms/` is pure stateless math
 * and WITHOUT importing three.js. This function is the MITIGATION R2 of the PLAN:
 * the G5/C1 formula is encoded as an isolated pure function, used by
 * `model/connectors.ts` (placeConnectorGroup) for placement.
 *
 * Context (SPEC G5):
 * - G5 — C1 (flush): "top connector-joist = top beam joist" (connection
 * retractable: the lock-joist DOES NOT protrude above the beam). The top of the
 * joist beam coincides with the connector plane (y = h): both beams
 * have `center_y = h − dims.h/2` → top = h (beamCenterFor, v1).
 * - The connector-header center is distant from the top plane of the header beam
 * `headerHeight/2 + |headerHeight − assemblyHeight|` where
 * `headerHeight = connector_module.height` e
 * `assemblyHeight = connector_assembly.height`.
 *
 * SIGN of the header offset — EXPLICIT DECISION:
 * - We choose NEGATIVE SIGN (the header center is BELOW the top plane at h).
 * - Geometric pattern: the lock-joist (flush) occupies [h − moduleHeight, h];
 * to ENGAGE with it, the lock-header must overlap vertically,
 * so it occupies [h − assemblyHeight, h − stagger] (assembly = 215 in
 * mock, stagger = 13.1). If the center header was ABOVE h, the two locks
 * they would not overlap (broken fit).
 * - TODO(R2, SPEC G5): the sign must however be CONFIRMED in the visual test
 * AC-1 (mock uses moduleHeight=201.9, assemblyHeight=215 → headerCenter
 * = h − 114.05).
 *
 * Convention: Offsets are COMPARED to the height `h` of the connector plane
 * (world root). The caller adds the converted `module.position`
 * (`moduleOffset[1]`), as `placeConnectorGroup` already does today.
 *
 * CENTRALIZED SIGN (R2): the negative sign of the center header is the constant
 * SSOT `HEADER_FLUSH_SIGN` in `config/defaults.ts` (to be calibrated in AC-1).
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

import { HEADER_FLUSH_SIGN } from "../config/defaults";

/**
 * Result of the G5/C1 formula (vertical offsets with respect to h, in mm).*/
export interface ConnectorFlushResult {
  /** Y offset of LOCK-JOIST center relative to h: `−moduleHeight/2` (top flush at h), minus `joistNotchDownwardShiftMm` if active (>0 only — alpha negativo/zero → 0, a filo header di default).*/
  joistCenterOffsetY: number;
  /**
   * Y offset of the LOCK-HEADER center with respect to h (formula G5, negative
   * sign → below the top plane), minus `joistNotchDownwardShiftMm` if active
   * — SAME shift as the joist (corretto 01-10-2026, richiesta utente: "lo
   * spostamento dell'angolo alfa fa traslare il lato joist ma dovrebbe far
   * traslare tutto l'assieme, insieme" — l'assieme joist+header e' un pezzo
   * fisico unico, con lo stagger GIA' fisso fra i due: lo shift trasla
   * l'intero assieme rigidamente, non allunga/comprime la sovrapposizione fra
   * i due lati applicandolo solo al joist).*/
  headerCenterOffsetY: number;
  /** Y offset of top LOCK-JOIST relative to h — 0 (flush, SPEC G5) unless `joistNotchDownwardShiftMm` is active, then `−joistNotchDownwardShiftMm`.*/
  joistTopOffsetY: number;
  /** Y offset of top LOCK-HEADER relative to h — `−stagger`, minus `joistNotchDownwardShiftMm` if active (stesso shift rigido del joist, vedi `headerCenterOffsetY`).*/
  headerTopOffsetY: number;
  /** Vertical difference in height between the tops of the two locks (mm) = stagger (`|assemblyHeight − moduleHeight|`).*/
  topGap: number;
}

/**
 * Formula G5/C1 (SPEC G5, AC-10, PLAN R2) — PURE function.
 *
 * @param moduleHeight `connector_module.height` (e.g. 201.9).
 * @param assemblyHeight `connector_assembly.height` (e.g. 215).
 * @param joistNotchDownwardShiftMm Traslazione verso il basso (01-10-2026,
 * richiesta utente: "quando abbiamo la configurazione notch e angolo
 * dobbiamo considerare che il connettore trasla verso il basso") — stessa
 * formula gia' usata lato backend per i filtri di compatibilita'
 * (`lock_compatibility_filters.go:joistNotchDownwardShift`,
 * `connectorEdgeDistance(lock) * tan(|alphaDeg|)`, attiva solo con
 * `LateralResistanceConfig = JoistBeamNotch` e alpha non nullo), calcolata
 * dal chiamante (`connectorPlacement.ts`) — qui si applica soltanto, non si
 * ricalcola. SOLO POSITIVO (corretto 01-10-2026, specifica utente: "quando si
 * ha un valore positivo il connettore deve stare a filo sulla joist ma
 * quando si ha un valore negativo il connettore deve stare a filo con il
 * header"): il chiamante lo calcola solo per alpha > 0, zero altrimenti — vedi
 * doc-comment sul segno in `resolveScene.ts`. Applicato a ENTRAMBI
 * `joistCenterOffsetY` e `headerCenterOffsetY` (corretto 01-10-2026, vedi
 * doc-comment su `headerCenterOffsetY`): trasla l'intero assieme, non solo il
 * joist. Default 0 (nessuno shift, comportamento identico a prima).
 * @returns Vertical offsets of the centers/tops with respect to the h plane.*/
export function resolveConnectorHeights(
  moduleHeight: number,
  assemblyHeight: number,
  joistNotchDownwardShiftMm = 0,
): ConnectorFlushResult {
  // Stagger (G4): |assembly − module|, arrotondato a 1 decimale come computeStagger.
  const stagger = Math.round(Math.abs(assemblyHeight - moduleHeight) * 10) / 10;

  //G5: top connector-joist = top beam joist (h plane) → center joist = h − moduleHeight/2,
  //MENO lo shift verso il basso quando notch+angolo sono attivi (vedi doc-comment parametro).
  const joistCenterOffsetY = -moduleHeight / 2 - joistNotchDownwardShiftMm;

  //G5: header center is away from the top plane (h) by `moduleHeight/2 + |moduleHeight − assemblyHeight|`.
  // SEGNO da `HEADER_FLUSH_SIGN` (SSOT config, R2 — da calibrare in AC-1):
  //-1 → below the top plane (lock-joist fit, default). STESSO shift del
  //joist sottratto qui (01-10-2026): l'assieme trasla rigidamente insieme,
  //vedi doc-comment su `headerCenterOffsetY`.
  const headerCenterOffsetY =
    HEADER_FLUSH_SIGN * (moduleHeight / 2 + Math.abs(moduleHeight - assemblyHeight)) - joistNotchDownwardShiftMm;

  const joistTopOffsetY = joistCenterOffsetY + moduleHeight / 2; // = 0 → flush ✓
  const headerTopOffsetY = headerCenterOffsetY + moduleHeight / 2; // = −stagger → incastro ✓

  return {
    joistCenterOffsetY,
    headerCenterOffsetY,
    joistTopOffsetY,
    headerTopOffsetY,
    //topGap is EXACTLY the stagger (G4): the top joist is at 0 (flush) and the
    //top header a −stagger → height difference = stagger. We use the variable already
    //calculated above (no redundant recalculation).
    topGap: stagger,
  };
}
